import React from 'react';

import { UseFormRegisterReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BLACK,
  BORDER_GRAY,
  COOL_GRAY100,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { EventNames } from '../../../utils/trackingEvents';
import Errors from '../ErrorMessage';
import Text, { TextColor, TextKind } from '../Text';

export enum InputSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

const StyledInputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledLabelContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

export const TextFieldCSS = css`
  box-sizing: border-box;
  width: 100%;

  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;
  font-family: reader;
  font-size: 14px;
  background-color: ${WHITE};
  &:focus {
    border-color: ${BLACK};
  }
  &:disabled {
    color: ${TEXT_DISABLED};
    background: ${COOL_GRAY100};
  }
`;

const StyledTextField = styled.input<{
  $size?: InputSize;
  $incognitoUntilFocused: boolean;
}>`
  ${TextFieldCSS}

  ${({ $incognitoUntilFocused }) => {
    if ($incognitoUntilFocused) {
      return css` 
        border-color: transparent;
      `;
    }
  }}

  ${({ $size }) => {
    switch ($size) {
      case InputSize.Large:
        return css`
          padding: 16px;
        `;
      case InputSize.Small:
        return css`
          height: 34px;
          border-radius: 4px;
          padding: 8px;
        `;
      case InputSize.Medium:
      default:
        return css`
          padding: 8px;
        `;
    }
  }}
`;

interface InputPropsRegister {
  label?: string;
  // default placeholder is the label
  placeholder?: string;
  // used for `useForm` register util
  formRegister?: UseFormRegisterReturn;
  onChange?: undefined;
  onBlur?: undefined;
  ref?: undefined;
  name?: undefined;
  disabled?: undefined;
  value?: undefined;

  onFocus?: () => void;
  autoFocus?: boolean;
  type?: string;
  size?: InputSize;
  optional?: boolean;
  id?: string;
  trackingData?: TrackingEventData;
  maxLength?: number;
  incognitoUntilFocused?: boolean;

  renderError?: boolean;
}

interface InputPropsManual {
  label?: string;
  // default placeholder is the label
  placeholder?: string;
  // used for `useForm` register util
  formRegister?: undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  ref: React.LegacyRef<HTMLInputElement>;
  name: string; // required if no formRegister to identify the input by
  disabled?: boolean;
  value?: string;

  onFocus?: () => void;
  autoFocus?: boolean;
  type?: string;
  size?: InputSize;
  optional?: boolean;
  id?: string;
  trackingData?: TrackingEventData;
  maxLength?: number;
  incognitoUntilFocused?: boolean;

  renderError?: boolean;
}

type InputProps = InputPropsRegister | InputPropsManual;

const Input: React.FC<InputProps> = ({
  formRegister,
  onChange,
  onBlur,
  ref,
  name,
  disabled,
  value,
  label,
  type,
  placeholder,
  onFocus,
  autoFocus,
  size,
  optional,
  id,
  trackingData,
  maxLength,
  incognitoUntilFocused = false,
  renderError = true,
}) => {
  const { track } = useTracking();

  const fieldName = formRegister?.name || name;

  const handleFocus = () => {
    if (trackingData) {
      track(EventNames.TextBoxActivated, trackingData);
    }
  };

  return (
    <StyledInputContainer>
      {label && (
        <StyledLabelContainer>
          <Text component="label" kind={TextKind.LabelL} htmlFor={id}>
            {label}
          </Text>
          {optional && (
            <Text
              component="label"
              kind={TextKind.BodyS}
              color={TextColor.Secondary}
            >
              (Optional)
            </Text>
          )}
        </StyledLabelContainer>
      )}
      <StyledTextField
        id={id}
        data-testid={id}
        autoFocus={autoFocus}
        type={type}
        placeholder={placeholder ?? label}
        onFocus={onFocus}
        $size={size}
        $incognitoUntilFocused={incognitoUntilFocused}
        required={!optional}
        onFocusCapture={handleFocus}
        maxLength={maxLength}
        onChange={onChange}
        onBlur={onBlur}
        ref={ref}
        name={name}
        disabled={disabled}
        value={value}
        {...formRegister}
      />
      {fieldName && renderError && (
        <Errors name={fieldName} errorTextKind={TextKind.BodyS} />
      )}
    </StyledInputContainer>
  );
};

export default Input;
